/**
 * Wraps an async function so that identical calls made while an earlier one is
 * still in flight all share that single request.
 *
 * Nothing is cached: as soon as the request settles the entry is dropped, so
 * the next call goes to the network again and never sees a stale answer. This
 * only collapses the duplicates that come from the same data being asked for
 * twice within the same moment — a page that remounts, a redirect to the
 * canonical url, two components on screen showing the same account.
 *
 * @param  {Function} fn
 * @param  {Function} buildKey  turns the arguments into a cache key
 * @return {Function}
 */
export const dedupeInFlight = function (fn, buildKey = (...args) => JSON.stringify(args)) {
    const inFlight = new Map();

    return function dedupedCall(...args) {
        const key = buildKey(...args);

        if (inFlight.has(key)) {
            return inFlight.get(key);
        }

        const promise = Promise.resolve().then(() => fn.apply(this, args));

        const forget = () => inFlight.delete(key);

        // Both handlers, so a rejection clears the entry without leaving an
        // unhandled rejection behind on this derived promise:
        promise.then(forget, forget);

        inFlight.set(key, promise);

        return promise;
    };
};
